import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Monitor, Building, Heart, GraduationCap, Newspaper, Leaf,
  X, ArrowRight, Ticket, CreditCard, Calendar
} from 'lucide-react';

const companies = [
  {
    id: 'ks-infosystems',
    name: 'KS Infosystems',
    icon: Monitor,
    color: '#007AFF',
    logo: 'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/u6qs14cs_7b052c47-d987-4682-9848-53098fb0943e.jpeg',
    tagline: 'ICT Infrastructure & Digital Solutions',
    countries: ['Ghana', 'DRC', 'Nigeria'],
    description: 'End-to-end ICT products and services including digital revenue collection, RFID ticketing, GPS fleet management, and biometric payroll solutions.'
  },
  {
    id: 'indocorp',
    name: 'Indocorp Enterprises',
    icon: Building,
    color: '#50C878',
    logo: 'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/axd86bzl_181fd668-7673-4fc9-961f-04950c3f166b.jpeg',
    tagline: 'MSME Empowerment & Industrial Capacity',
    countries: ['Ghana', 'Kenya', 'Tanzania'],
    description: 'Building industrial capacity through turnkey solutions, tractor assembly, waste management, and food processing units.'
  },
  {
    id: 'times-africa',
    name: 'The Times of Africa',
    icon: Newspaper,
    color: '#F39C12',
    logo: 'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/8yod4ui6_d5b91698-f93e-4168-84d3-01ff07a1b57b.jpeg',
    tagline: 'Africa-India Media Bridge',
    countries: ['26 Nations'],
    description: 'Monthly magazine covering Africa-India relations with 10,000+ circulation and 100+ leader interviews.'
  },
  {
    id: 'ks-academy',
    name: 'KS Academy',
    icon: GraduationCap,
    color: '#9B59B6',
    logo: null,
    tagline: 'Skills Development & Training',
    countries: ['Ghana', 'Nigeria'],
    description: 'Comprehensive training programs for mobile repair, technology skills, and entrepreneurship development.'
  },
  {
    id: 'heal-bliss',
    name: 'Heal & Bliss',
    icon: Heart,
    color: '#E4405F',
    logo: null,
    tagline: 'Medical Tourism & Wellness',
    countries: ['India', 'Africa'],
    description: 'Healthcare bridge between Africa and India — medical tourism, diagnostics, hospital development, and wellness services.'
  },
  {
    id: 'ups',
    name: 'United Platform Solutions',
    icon: Leaf,
    color: '#1ABC9C',
    logo: 'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/k42spc1h_db6dc0c4-79ea-4317-9232-4167ada35f8d.jpeg',
    tagline: 'Climate Tech Pioneer',
    countries: ['Ghana', 'Nigeria', 'Tanzania'],
    description: 'AOCEMS emissions monitoring platform — real-time tracking, AI analytics, and regulatory compliance across Africa.'
  },
];

const innovations = [
  {
    id: 'iticketing',
    title: 'iTicketing',
    subtitle: 'Africa Cup of Nations 2008',
    icon: Ticket,
    color: '#D4AF37',
    stat: '1.4M',
    statLabel: 'Spectators Served',
    description: 'Pioneering RFID ticketing for Africa Cup of Nations 2008 in Ghana — 4 stadiums, 120 gates, 40 matches.'
  },
  {
    id: 'ks-collect',
    title: 'KS Collect',
    subtitle: 'World Bank PRCG 2014',
    icon: CreditCard,
    color: '#007AFF',
    stat: '7+',
    statLabel: 'Cities Deployed',
    description: 'Revolutionary digital tax collection system recognized by World Bank PRCG project in 2014.'
  },
];

const milestones = [
  { year: '2001', event: 'First business in Africa (Ghana)' },
  { year: '2008', event: 'Africa Cup of Nations iTicketing' },
  { year: '2010', event: 'LESDEP launch' },
  { year: '2014', event: 'World Bank PRCG recognition' },
  { year: '2018+', event: 'Climate tech AOCEMS' },
  { year: '2020', event: '1M Instagram followers' },
  { year: '2026', event: 'Coaching & Advisory' },
];

export const WorkSection = () => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section 
      id="work" 
      className="section relative overflow-hidden"
      data-testid="work-section"
    >
      <div className="max-w-7xl mx-auto px-6" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="mb-16"
        >
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">Track Record</p>
          <h2 className="section-title">
            Work — <span className="gradient-text">Proof of Experience</span>
          </h2>
          <p className="section-subtitle mt-4">
            30 years of building companies, not just advising from the sidelines.
          </p>
        </motion.div>

        {/* Companies Grid - 6 Most Relevant */}
        <div className="mb-16">
          <h3 className="text-xl font-semibold text-white mb-6">Companies Built</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {companies.map((company, index) => (
              <motion.div
                key={company.id}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                className="company-card card-hover cursor-pointer"
                onClick={() => setSelectedCompany(company)}
                data-testid={`company-card-${company.id}`}
              >
                {company.logo ? (
                  <div className="w-full h-12 mb-3 flex items-center">
                    <img 
                      src={company.logo} 
                      alt={`${company.name} logo`}
                      className="h-10 w-auto max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${company.color}15`, color: company.color }}
                  >
                    <company.icon size={20} />
                  </div>
                )}
                <h4 className="text-base font-semibold text-white mb-1">{company.name}</h4>
                <p className="text-xs text-white/50 mb-2">{company.tagline}</p>
                <div className="flex flex-wrap gap-1">
                  {company.countries.map((country) => (
                    <span key={country} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/40">
                      {country}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Innovation Highlights - 2 Most Impressive */}
        <div className="mb-16">
          <h3 className="text-xl font-semibold text-white mb-6">Innovation Highlights</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {innovations.map((innovation, index) => (
              <motion.div
                key={innovation.id}
                initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass rounded-2xl p-6"
                data-testid={`innovation-${innovation.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${innovation.color}15` }}
                    >
                      <innovation.icon size={24} style={{ color: innovation.color }} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white">{innovation.title}</h4>
                      <p className="text-sm text-[#D4AF37]">{innovation.subtitle}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: innovation.color }}>{innovation.stat}</p>
                    <p className="text-xs text-white/50">{innovation.statLabel}</p>
                  </div>
                </div>
                <p className="text-sm text-white/60">{innovation.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Key Milestones Timeline */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
        >
          <h3 className="text-xl font-semibold text-white mb-6">Key Milestones</h3>
          <div className="flex flex-wrap gap-4 justify-center">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.7 + index * 0.05 }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10"
              >
                <span className="text-[#D4AF37] font-bold font-mono">{milestone.year}</span>
                <span className="text-sm text-white/70">{milestone.event}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Company Detail Modal */}
      <AnimatePresence>
        {selectedCompany && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            onClick={() => setSelectedCompany(null)}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-lg glass rounded-2xl p-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="company-modal"
            >
              <button
                onClick={() => setSelectedCompany(null)}
                className="absolute top-4 right-4 text-white/60 hover:text-white"
                data-testid="close-modal"
              >
                <X size={24} />
              </button>

              {selectedCompany.logo ? (
                <div className="w-full h-16 mb-4 flex items-center">
                  <img 
                    src={selectedCompany.logo} 
                    alt={`${selectedCompany.name} logo`}
                    className="h-14 w-auto max-w-[180px] object-contain"
                  />
                </div>
              ) : (
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${selectedCompany.color}15`, color: selectedCompany.color }}
                >
                  <selectedCompany.icon size={28} />
                </div>
              )}

              <h3 className="text-2xl font-bold text-white mb-1">{selectedCompany.name}</h3>
              <p className="text-[#D4AF37] mb-4">{selectedCompany.tagline}</p>
              <p className="text-white/70 mb-4">{selectedCompany.description}</p>
              
              <div className="flex flex-wrap gap-2">
                {selectedCompany.countries.map((country) => (
                  <span 
                    key={country}
                    className="px-3 py-1 rounded-full text-sm"
                    style={{ backgroundColor: `${selectedCompany.color}15`, color: selectedCompany.color }}
                  >
                    {country}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
